import { useEffect, useRef } from 'react'
import {
  describePayload,
  readKeyboardLayout,
  reportScan,
  scanBreadcrumb,
  type PayloadDiagnostics,
} from './scanTelemetry'

// A hardware barcode scanner (the gyms use a Zebex Z-3272SR) is a keyboard: it
// types the decoded text and usually ends with Enter. We tell it apart from a
// human by speed — keys arriving less than WEDGE_GAP_THRESHOLD_MS apart belong
// to the same scan, anything slower starts over.
//
// That threshold is also the most likely thing to be wrong at a live gym: a
// scanner configured with an inter-character delay, or a loaded machine, drops
// past it and the buffer is wiped mid-payload. Every reset is therefore counted
// and reported rather than silently swallowed.

export const WEDGE_GAP_THRESHOLD_MS = 50
export const WEDGE_FLUSH_TIMEOUT_MS = 200

/** Keys arriving at scanner speed before we call a burst machine-made. */
const BURST_MIN_KEYS = 6
/** A partial buffer at least this long was a real scan, not a stray keypress. */
const MEANINGFUL_PARTIAL = 4
const IGNORED_REPORT_COOLDOWN_MS = 5000

export type WedgeTerminator = 'enter' | 'tab' | 'timeout'

export interface WedgeReport {
  terminator: WedgeTerminator
  /** Every keydown seen since the last flush, including the ones we dropped. */
  key_events: number
  buffer_length: number
  duration_ms: number
  gap_min_ms: number | null
  gap_max_ms: number | null
  gap_max_index: number | null
  gap_avg_ms: number | null
  gap_threshold_ms: number
  /** How many times the gap rule wiped a partial buffer during this scan. */
  resets: number
  discarded_chars: number
  /** Physical keys, unaffected by the OS keyboard layout. */
  codes: string[]
  /** Characters the layout produced. Diverging from `codes` is the bug. */
  keys: string[]
  /** Keys we could not turn into a character (`Shift`, `Dead`, `Unidentified`). */
  dropped: string[]
  modifiers: string[]
  active_element: string
  layout: Record<string, string> | null
  payload: PayloadDiagnostics
}

export interface WedgeOptions {
  /**
   * `active` feeds the payload to `onPayload`. `observe` only reports what it
   * saw — used outside the QR tab, where a scan today does nothing at all and
   * we want Sentry to say so instead of the gym guessing.
   */
  mode: 'active' | 'observe'
  enabled?: boolean
  /** Where the listener is mounted, for the Sentry report. */
  source: string
  onPayload?: (payload: string, report: WedgeReport) => void
  /** Whether the UI can take a scan right now. */
  canAccept?: () => boolean
}

function describeElement(el: Element | null): string {
  if (!el) return 'none'
  const tag = el.tagName.toLowerCase()
  const id = el.id ? `#${el.id}` : ''
  const type = el.getAttribute('type')
  return `${tag}${id}${type ? `[${type}]` : ''}`
}

function average(values: number[]): number | null {
  if (values.length === 0) return null
  return Math.round(values.reduce((sum, v) => sum + v, 0) / values.length)
}

export function useWedgeScanner(options: WedgeOptions): void {
  // Kept in a ref so the listener is installed once and still sees the current
  // handlers: re-binding it on every render would lose the buffer mid-scan.
  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  })

  const layoutRef = useRef<Record<string, string> | null>(null)

  useEffect(() => {
    let cancelled = false
    void readKeyboardLayout().then((layout) => {
      if (cancelled) return
      layoutRef.current = layout
      if (layout) scanBreadcrumb('keyboard layout read', layout)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (options.enabled === false) return

    const { mode, source } = optionsRef.current
    scanBreadcrumb('wedge listener mounted', { mode, source })

    let buffer = ''
    let lastKeyTime = 0
    let firstKeyTime = 0
    let flushTimer: ReturnType<typeof setTimeout> | undefined

    // Per-attempt diagnostics, reset on every flush.
    let codes: string[] = []
    let keys: string[] = []
    let dropped: string[] = []
    let gaps: number[] = []
    let modifiers = new Set<string>()
    let keyEvents = 0
    let resets = 0
    let discarded = 0
    let activeElement = 'none'

    // The ignored-input path keeps its own counters: we never touch the text
    // someone is typing, we only notice when it arrives at machine speed.
    let ignoredBurst = 0
    let ignoredLastKeyTime = 0
    let ignoredReportedAt = 0

    function resetAttempt() {
      buffer = ''
      codes = []
      keys = []
      dropped = []
      gaps = []
      modifiers = new Set()
      keyEvents = 0
      resets = 0
      discarded = 0
      firstKeyTime = 0
    }

    function buildReport(terminator: WedgeTerminator): WedgeReport {
      const maxGap = gaps.length ? Math.max(...gaps) : null
      return {
        terminator,
        key_events: keyEvents,
        buffer_length: buffer.length,
        duration_ms: firstKeyTime ? lastKeyTime - firstKeyTime : 0,
        gap_min_ms: gaps.length ? Math.min(...gaps) : null,
        gap_max_ms: maxGap,
        gap_max_index: maxGap === null ? null : gaps.indexOf(maxGap),
        gap_avg_ms: average(gaps),
        gap_threshold_ms: WEDGE_GAP_THRESHOLD_MS,
        resets,
        discarded_chars: discarded,
        codes: [...codes],
        keys: [...keys],
        dropped: [...dropped],
        modifiers: [...modifiers],
        active_element: activeElement,
        layout: layoutRef.current,
        payload: describePayload(buffer),
      }
    }

    function flush(terminator: WedgeTerminator) {
      if (keyEvents === 0) return

      const { mode, source, onPayload, canAccept } = optionsRef.current
      const report = buildReport(terminator)
      const base = { source, mode, ...report }

      if (!buffer) {
        // Keys arrived but nothing survived: every character was dropped by the
        // layout, or the gap rule wiped the buffer down to nothing.
        reportScan('scan produced an empty payload', {
          stage: 'wedge-flush',
          level: keyEvents >= BURST_MIN_KEYS ? 'warning' : 'info',
          data: base,
        })
        resetAttempt()
        return
      }

      if (mode === 'observe') {
        reportScan('scan seen outside the QR tab, ignored', {
          stage: 'wedge-ignored',
          level: 'warning',
          data: base,
        })
        resetAttempt()
        return
      }

      if (canAccept && !canAccept()) {
        reportScan('scan dropped, the UI was not ready for one', {
          stage: 'wedge-ignored',
          level: 'warning',
          data: base,
        })
        resetAttempt()
        return
      }

      if (resets > 0) {
        reportScan('scan buffer was wiped mid-payload before it flushed', {
          stage: 'wedge-reset',
          level: 'warning',
          data: base,
        })
      } else {
        reportScan('scan captured', { stage: 'wedge-flush', level: 'info', data: base })
      }

      const payload = buffer
      resetAttempt()
      onPayload?.(payload, report)
    }

    function onIgnoredTarget(e: KeyboardEvent, target: Element) {
      const now = Date.now()
      const gap = now - ignoredLastKeyTime
      ignoredLastKeyTime = now
      ignoredBurst = gap <= WEDGE_GAP_THRESHOLD_MS ? ignoredBurst + 1 : 1

      if (ignoredBurst !== BURST_MIN_KEYS) return
      if (now - ignoredReportedAt < IGNORED_REPORT_COOLDOWN_MS) return
      ignoredReportedAt = now

      // Deliberately no raw text: this is whatever the staff member is typing
      // into a field, and the shape is enough to recognise a scan.
      reportScan('scan-speed keys went into a focused field, not the scanner', {
        stage: 'wedge-ignored',
        level: 'warning',
        data: {
          source: optionsRef.current.source,
          mode: optionsRef.current.mode,
          active_element: describeElement(target),
          first_code: e.code,
          burst_keys: ignoredBurst,
        },
      })
    }

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as Element | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        onIgnoredTarget(e, target!)
        return
      }

      const now = Date.now()
      const gap = lastKeyTime ? now - lastKeyTime : 0
      lastKeyTime = now
      if (!firstKeyTime) firstKeyTime = now
      keyEvents += 1
      activeElement = describeElement(document.activeElement)

      if (e.ctrlKey) modifiers.add('ctrl')
      if (e.altKey) modifiers.add('alt')
      if (e.metaKey) modifiers.add('meta')
      if (e.shiftKey) modifiers.add('shift')

      clearTimeout(flushTimer)

      if (e.key === 'Enter' || e.key === 'Tab') {
        // A scanner's terminator must not double as "press the focused button".
        if (buffer) e.preventDefault()
        flush(e.key === 'Enter' ? 'enter' : 'tab')
        return
      }

      if (gap > WEDGE_GAP_THRESHOLD_MS && buffer.length > 0) {
        if (buffer.length >= MEANINGFUL_PARTIAL) {
          reportScan('partial scan discarded, keys arrived too slowly', {
            stage: 'wedge-reset',
            level: 'warning',
            data: {
              source: optionsRef.current.source,
              gap_ms: gap,
              gap_threshold_ms: WEDGE_GAP_THRESHOLD_MS,
              discarded_length: buffer.length,
              discarded_shape: describePayload(buffer).shape,
              next_code: e.code,
            },
          })
        }
        resets += 1
        discarded += buffer.length
        buffer = ''
      }

      if (gap > 0) gaps.push(gap)
      codes.push(e.code)

      if (e.key.length === 1) {
        buffer += e.key
        keys.push(e.key)
      } else {
        dropped.push(e.key)
      }

      flushTimer = setTimeout(() => flush('timeout'), WEDGE_FLUSH_TIMEOUT_MS)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      clearTimeout(flushTimer)
      scanBreadcrumb('wedge listener unmounted', { mode, source })
    }
  }, [options.enabled])
}
